/**
 * Genuine In-Browser MP3 Audio Encoder using @breezystack/lamejs
 * Converts raw microphone PCM audio into real MPEG-1 Layer 3 binary frames (0xFF 0xFB)
 * 100% compatible with Meta WhatsApp Cloud API and AWS S3
 */
import { Mp3Encoder } from "@breezystack/lamejs";

export class VoiceNoteRecorder {
  constructor() {
    this.audioContext = null;
    this.mediaStream = null;
    this.processor = null;
    this.source = null;
    this.pcmChunks = [];
    this.sampleRate = 44100;
  }

  async start() {
    this.pcmChunks = [];
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new AudioContextClass({ sampleRate: 44100 });
    this.sampleRate = this.audioContext.sampleRate || 44100;

    this.source = this.audioContext.createMediaStreamSource(this.mediaStream);
    // Buffer size 4096, 1 input channel, 1 output channel
    this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);

    this.processor.onaudioprocess = (e) => {
      const inputData = e.inputBuffer.getChannelData(0);
      // Convert Float32 [-1.0, 1.0] to Int16 PCM [-32768, 32767]
      const int16 = new Int16Array(inputData.length);
      for (let i = 0; i < inputData.length; i++) {
        const s = Math.max(-1, Math.min(1, inputData[i]));
        int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      this.pcmChunks.push(int16);
    };

    this.source.connect(this.processor);
    this.processor.connect(this.audioContext.destination);
  }

  async stop() {
    if (this.processor) {
      this.processor.disconnect();
      this.processor.onaudioprocess = null;
    }
    if (this.source) {
      this.source.disconnect();
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
    }
    if (this.audioContext && this.audioContext.state !== "closed") {
      await this.audioContext.close();
    }

    // Merge Int16 PCM samples
    let totalLength = 0;
    for (const chunk of this.pcmChunks) {
      totalLength += chunk.length;
    }

    const mergedPCM = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of this.pcmChunks) {
      mergedPCM.set(chunk, offset);
      offset += chunk.length;
    }

    // Encode to real MP3 using lamejs
    const channels = 1;
    const sampleRate = this.sampleRate || 44100;
    const kbps = 128;
    const mp3encoder = new Mp3Encoder(channels, sampleRate, kbps);
    const mp3Data = [];

    const sampleBlockSize = 1152;
    for (let i = 0; i < mergedPCM.length; i += sampleBlockSize) {
      const sampleChunk = mergedPCM.subarray(i, i + sampleBlockSize);
      const mp3buf = mp3encoder.encodeBuffer(sampleChunk);
      if (mp3buf && mp3buf.length > 0) {
        mp3Data.push(mp3buf);
      }
    }

    const mp3buf = mp3encoder.flush();
    if (mp3buf && mp3buf.length > 0) {
      mp3Data.push(mp3buf);
    }

    // Return pure MP3 Blob (audio/mpeg)
    return new Blob(mp3Data, { type: "audio/mpeg" });
  }

  cancel() {
    if (this.processor) {
      this.processor.disconnect();
      this.processor.onaudioprocess = null;
    }
    if (this.source) {
      this.source.disconnect();
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
    }
    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close().catch(() => {});
    }
    this.pcmChunks = [];
  }
}
